import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { searchByCompany, formatCurrency, checkExclusionsBatch, checkOwnershipBatch, checkResearchBatch } from "../utils/cmsApi";
import { useSubscription } from "../context/SubscriptionContext";
import { useMyProviders } from "../context/MyProvidersContext";
import { useUser } from "@clerk/react";
import { getWatchedProvider, unwatchProvider, getWatchedProviders } from "../utils/alertsApi";
import { apiFetch } from "../utils/apiClient";
import WatchProviderButton from "../components/WatchProviderButton";
import CompareButton from "../components/CompareButton";
import { KNOWN_COMPANIES, titleCaseCompany } from "../utils/companies";


const US_STATES = [
  "ALL","AK","AL","AR","AZ","CA","CO","CT","DC","DE","FL","GA","HI",
  "IA","ID","IL","IN","KS","KY","LA","MA","MD","ME","MI","MN","MO",
  "MS","MT","NC","ND","NE","NH","NJ","NM","NV","NY","OH","OK","OR",
  "PA","RI","SC","SD","TN","TX","UT","VA","VT","WA","WI","WV","WY",
];

const SPECIALTIES = [
  "ALL",
  "Allopathic & Osteopathic Physicians|Anesthesiology",
  "Allopathic & Osteopathic Physicians|Cardiology",
  "Allopathic & Osteopathic Physicians|Dermatology",
  "Allopathic & Osteopathic Physicians|Emergency Medicine",
  "Allopathic & Osteopathic Physicians|Family Medicine",
  "Allopathic & Osteopathic Physicians|Gastroenterology",
  "Allopathic & Osteopathic Physicians|General Surgery",
  "Allopathic & Osteopathic Physicians|Internal Medicine",
  "Allopathic & Osteopathic Physicians|Neurology",
  "Allopathic & Osteopathic Physicians|Obstetrics & Gynecology",
  "Allopathic & Osteopathic Physicians|Oncology",
  "Allopathic & Osteopathic Physicians|Ophthalmology",
  "Allopathic & Osteopathic Physicians|Orthopedic Surgery",
  "Allopathic & Osteopathic Physicians|Pathology",
  "Allopathic & Osteopathic Physicians|Pediatrics",
  "Allopathic & Osteopathic Physicians|Psychiatry & Neurology",
  "Allopathic & Osteopathic Physicians|Pulmonology",
  "Allopathic & Osteopathic Physicians|Radiology",
  "Allopathic & Osteopathic Physicians|Rheumatology",
  "Allopathic & Osteopathic Physicians|Urology",
];

function specialtyLabel(s) {
  if (s === "ALL") return "All specialties";
  return s.includes("|") ? s.split("|")[1] : s;
}

export default function CompanySearchPage({ year }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyQuery = searchParams.get("company") || "";
  const [searchInput, setSearchInput] = useState(companyQuery ? titleCaseCompany(companyQuery) : "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(!!companyQuery);
  const [error, setError] = useState(null);
  const [state, setState] = useState("ALL");
  const [specialty, setSpecialty] = useState("ALL");
  const [excludedOnly, setExcludedOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const inputRef = useRef(null);
  const activeFilterCount = [state !== "ALL", specialty !== "ALL", excludedOnly].filter(Boolean).length;

// Auto-open filters if active
  useEffect(() => {
    if (state !== "ALL" || specialty !== "ALL" || excludedOnly) {
      setFiltersOpen(true);
    }
  }, [state, specialty, excludedOnly]);

  const { isPro, setShowUpgradeModal, setShowSignInModal, setUpgradeMessage, canSearch, incrementSearch, searchesRemaining } = useSubscription();  
  const { user, isSignedIn } = useUser();
  const {
    filterActive, providerNpis, activeList, selectedListIdsString,
    selectedLists, totalSelectedProviders, syncing, syncError,
  } = useMyProviders();
  const [excludedNpis, setExcludedNpis] = useState(new Map());
  const [ownershipNpis, setOwnershipNpis] = useState(new Set());
  const [researchNpis, setResearchNpis] = useState(new Set());
  const [watchedNpis, setWatchedNpis] = useState(new Map()); 
  const [watchedProviders, setWatchedProviders] = useState([]);
  const [watchListOpen, setWatchListOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [exportError, setExportError] = useState(null);

  async function handleFullExport(company, yr) {
    setExportLoading(true);
    setExportError(null);
    try {
      console.log("Sending userId:", user?.id);
      const res = await apiFetch(`/api/export-company?companyName=${encodeURIComponent(company)}&year=${yr}&userId=${user?.id || ""}`);            
      const data = await res.json();
      if (data.url) {
        const a = document.createElement("a");
        a.href = data.url;
        a.download = data.fileName;
        a.click();
      } else {
        setExportError("Export failed. Please try again.");
      }
    } catch (err) {
      setExportError("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  }

  // Filter known companies as user types
  useEffect(() => {
    if (searchInput.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const q = searchInput.toUpperCase();
    const matches = KNOWN_COMPANIES.filter((c) => c.includes(q));
    setSuggestions(matches);
  }, [searchInput]);

// Fetch results when URL company param changes
  useEffect(() => {
    if (!companyQuery.trim()) return;
  setLoading(true);
    setError(null);
    setResults([]);
    setSummary(null);
    setExcludedNpis(new Map());    
    const searchFn = filterActive && selectedListIdsString
      ? fetch(`/api/bigquery?action=searchByCompanyWithinMyProviders&userId=${encodeURIComponent(user?.id || "")}&listIds=${encodeURIComponent(selectedListIdsString)}&companyName=${encodeURIComponent(companyQuery)}&year=${year}&state=${encodeURIComponent(state)}&specialty=${encodeURIComponent(specialty)}`).then((r) => r.json())
      : searchByCompany(companyQuery, year, { state, specialty, excludedOnly });

    searchFn
      .then((data) => {
        setSummary({ totalPhysicians: data.totalPhysicians || data.length, totalPaid: data.totalPaid || 0, companyName: data.companyName || companyQuery });
        setResults(data.providers || data);
        const npis = data.providers.map((p) => p.npi).filter(Boolean);
        // Check exclusions for all results
        if (npis.length > 0) {
          checkExclusionsBatch(npis)
            .then((exclusions) => {
              const map = new Map(exclusions.map((e) => [e.npi, { oig: e.oig, sam: e.sam }]));
              setExcludedNpis(map);
            })
            .catch(() => {});
          checkOwnershipBatch(npis)
            .then((ownNpis) => setOwnershipNpis(new Set(ownNpis)))
            .catch(() => {});
          checkResearchBatch(npis)
            .then((resNpis) => setResearchNpis(new Set(resNpis)))
            .catch(() => {});
        }
        // Check watched status for all results
        if (isSignedIn && user) {
          Promise.all(npis.map((npi) => getWatchedProvider(user.id, npi).then((data) => ({ npi, watched: !!data }))))          
            .then((results) => {
              const map = new Map(results.map((r) => [r.npi, r.watched]));
              setWatchedNpis(map);
            })
            .catch(() => {});
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [companyQuery, year, state, specialty, excludedOnly, filterActive, selectedListIdsString]);

  // Load full watched providers list
  useEffect(() => {
    if (!isSignedIn || !user) return;
    getWatchedProviders(user.id)
      .then((data) => setWatchedProviders(data))
      .catch(() => {});
  }, [isSignedIn, user, watchedNpis]);  

async function handleSearch(e) {
    e.preventDefault();
    if (!searchInput.trim()) return;
    if (!canSearch()) {
      if (!isSignedIn) {
        setShowSignInModal(true);
      } else {
        setUpgradeMessage(`You've reached your daily search limit. Upgrade to Pro for unlimited searches and full access.`);
        setShowUpgradeModal(true);
      }
      return;
    }    
    await incrementSearch();
    navigate(`/company-results?q=${encodeURIComponent(searchInput.trim())}`);
  }

  async function selectSuggestion(name) {
    if (!canSearch()) {
      if (!isSignedIn) {
        setShowSignInModal(true);
      } else {
        setUpgradeMessage(`You've reached your daily search limit. Upgrade to Pro for unlimited searches and full access.`);
        setShowUpgradeModal(true);
      }
      return;
    }    
    await incrementSearch();
    setSearchInput(titleCaseCompany(name));
    setShowSuggestions(false);
    navigate(`/company-search?company=${encodeURIComponent(name)}`);
  }

  const displayName = summary?.companyName
    ? titleCaseCompany(summary.companyName)
    : titleCaseCompany(companyQuery);

  const filteredResults = results; // BQ handles filtering server-side when filter active

return (
    <>
    <main className="max-w-3xl mx-auto px-4 py-10">      
      <div className="mb-6">
        <Link to="/" className="text-white/40 hover:text-white/70 text-sm transition-colors">
          ← Home
        </Link>
        <h1 className="font-display text-3xl text-white mt-3 mb-6">
          Search by <span className="text-gold-400">company</span>
        </h1>

        {/* Company search bar with suggestions */}
        <form onSubmit={handleSearch} className="relative mb-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                className="input-base w-full"
                placeholder="e.g. Pfizer, Medtronic, AstraZeneca..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                autoComplete="off"
              />

              {/* Suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-navy-800 border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl">
                  {suggestions.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onMouseDown={() => selectSuggestion(name)}
                      className="w-full text-left px-4 py-3 text-sm text-white/70 hover:bg-navy-700 hover:text-white transition-colors border-b border-white/5 last:border-0"
                    >
                      {titleCaseCompany(name)}
                    </button>
                  ))}
                  <div className="px-4 py-2 text-white/20 text-xs border-t border-white/5">
                    Don't see your company? Type the full name and press Search.
                  </div>
                </div>
              )}
            </div>
            <button type="submit" className="btn-primary px-5 shrink-0">
              Search →
            </button>
          </div>
          {!isPro && (
            <p className="text-center text-white/30 text-xs mt-3">
              {searchesRemaining > 0
                ? `${searchesRemaining} free search${searchesRemaining !== 1 ? "es" : ""} remaining`
                : `Free limit reached — upgrade for unlimited searches`}
            </p>
          )}
        </form>

        {/* Popular companies */}
        {!companyQuery && (
          <div className="mb-6">
            <p className="text-white/30 text-xs mb-2">Popular searches:</p>
            <div className="flex flex-wrap gap-2">
              {["Pfizer Inc.", "Eli Lilly And Company", "Abbvie Inc.", "Medtronic, Inc.", "Novo Nordisk Inc"].map((name) => (
                <button
                  key={name}
                  onClick={() => selectSuggestion(name.toUpperCase())}
                  className="text-xs bg-navy-800 border border-white/10 hover:border-gold-500/30 text-white/50 hover:text-white/80 px-3 py-1.5 rounded-lg transition-all"
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
        >
          <span>⊞</span>
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="bg-gold-500 text-navy-900 text-xs font-medium px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
          <span className="text-white/30">{filtersOpen ? "▲" : "▼"}</span>
        </button>

        {filtersOpen && (
          <div className="mt-3 bg-navy-800 border border-white/10 rounded-xl p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/40 text-xs mb-1.5 block">State</label>
                <select
                  className="w-full bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s === "ALL" ? "All states" : s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-white/40 text-xs mb-1.5 block">Specialty</label>
                <select
                  className="w-full bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                >
                  {SPECIALTIES.map((s) => (
                    <option key={s} value={s}>{specialtyLabel(s)}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* Exclusion filter toggle */}
            {/*<div className="flex items-center justify-between bg-navy-700/50 border border-white/10 rounded-lg p-3">
              <div>
                <label className="text-white/70 text-sm font-medium block">
                  Excluded providers only
                </label>
                <p className="text-white/30 text-xs mt-0.5">
                  Show only providers on the OIG or SAM exclusion lists
                </p>
              </div>
              <button
                onClick={() => setExcludedOnly(!excludedOnly)}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                  excludedOnly ? "bg-gold-500" : "bg-white/10"
                }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                    excludedOnly ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div> */}

            {activeFilterCount > 0 && (
              <button
                onClick={() => { setState("ALL"); setSpecialty("ALL"); setExcludedOnly(false); }}
                className="text-xs text-white/40 hover:text-white/70 transition-colors"
              >
                ✕ Clear filters
              </button>
            )}            
          </div>
        )}
      </div>

      {/* My Providers filter banner */}
      {filterActive && (
        <div className="mb-4 flex items-center justify-between px-3 py-2 rounded-lg bg-gold-500/10 border border-gold-500/30">
          <div className="flex items-center gap-2">
            <span className="text-gold-400 text-xs">◉</span>
            <span className="text-gold-400 text-xs font-medium">
              {selectedLists.length === 1
                ? selectedLists[0].list_name
                : `${selectedLists.length} lists`}
            </span>
            <span className="text-gold-400/50 text-xs">
              · {totalSelectedProviders.toLocaleString()} providers
            </span>
          </div>
          <Link to="/my-providers" className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors">
            Manage →
          </Link>
        </div>
      )}
      {syncing && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/50 text-xs animate-pulse">
          ⏳ Preparing your provider filter...
        </div>
      )}
      {syncError && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
          ✗ {syncError}
        </div>
      )}

      {/* Results summary */}
      {!loading && !error && summary && (
        <div className="mb-4">
          <p className="text-white/40 text-sm">
            <span className="text-gold-400">{displayName}</span>
            {state !== "ALL" ? ` · ${state}` : ""}
            {specialty !== "ALL" ? ` · ${specialtyLabel(specialty)}` : ""}
            {" "}· {year} data
          </p>
          <div className="flex gap-6 mt-3">
            <div className="card py-3 px-5 text-center">
              <div className="text-white font-display text-2xl">{summary.totalPhysicians.toLocaleString()}</div>
              <div className="text-white/30 text-xs mt-0.5">total providers paid</div>
            </div>
            <div className="card py-3 px-5 text-center">
              <div className="text-gold-400 font-display text-2xl">{formatCurrency(summary.totalPaid)}</div>
              <div className="text-white/30 text-xs mt-0.5">total paid</div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-3">
              <p className="text-white/30 text-xs">
              {filterActive
                ? `${filteredResults.length} of your providers paid by ${displayName}`
                : isPro ? "Showing top 200 providers by payment amount" : "Showing top 50 providers by payment amount"}
            </p>
              {isSignedIn && isPro && (
                <button
                  onClick={() => {
                    document.getElementById("watching-list")?.scrollIntoView({ behavior: "smooth" });
                    setWatchListOpen(true);
                  }}
                  className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors shrink-0"
                >
                  🔔 Provider Watch List ↓
                </button>
              )}
            </div>                        
{isPro ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => exportProvidersCsv(results, displayName)}
                  className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors"
                >
                  ⬇ Export visible
                </button>
                <button
                  onClick={() => handleFullExport(companyQuery, year)}
                  className="text-gold-400/80 hover:text-gold-400 text-xs transition-colors font-medium"
                >
                  ⬇ Export all providers
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setUpgradeMessage("Exporting data is a Pro feature. Upgrade to download the full provider list as a CSV.");
                  setShowUpgradeModal(true);
                }}
                className="text-white/20 hover:text-white/40 text-xs transition-colors"
              >
                🔒 Export CSV
              </button>                            
            )}

          </div>               
        </div>
      )}

      {exportLoading && (
        <div className="card bg-gold-500/10 border border-gold-500/20 text-center py-4 mb-4">
          <p className="text-gold-400 text-sm animate-pulse">⏳ Preparing your export... this may take a moment for large datasets.</p>
        </div>
      )}
      {exportError && (
        <div className="card border-red-500/20 bg-red-500/5 text-red-300 text-sm mb-4">
          ⚠ {exportError}
        </div>
      )}

      {!companyQuery && (
        <div className="card text-center py-16">
          <div className="text-4xl mb-3 text-white/10">◈</div>
          <p className="text-white/40 text-sm">Enter a company name above to see which physicians they paid.</p>
        </div>
      )}

      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-white/10 rounded w-1/2 mb-3" />
              <div className="h-3 bg-white/5 rounded w-3/4 mb-2" />
              <div className="h-3 bg-white/5 rounded w-1/4" />
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="card border-red-500/20 bg-red-500/5 text-red-300 text-sm">
          ⚠ {error}
        </div>
      )}

      {!loading && !error && companyQuery && results.length === 0 && (
        <div className="card text-center py-12">
          <div className="text-4xl mb-3 text-white/20">◈</div>
          <p className="text-white/50 text-sm">No results found for "{titleCaseCompany(companyQuery)}".</p>
          <p className="text-white/30 text-xs mt-1">Try a different search term.</p>
        </div>
      )}

      {!loading && !error && results.length > 0 && filteredResults.length === 0 && filterActive && (
        <div className="card text-center py-12 border-gold-500/20 bg-gold-500/5">
          <div className="text-4xl mb-3">◉</div>
          <p className="text-gold-400 text-sm font-medium">None of your providers paid by {displayName}</p>
          <p className="text-white/40 text-xs mt-1">
            {results.length} provider{results.length !== 1 ? "s" : ""} received payments but none are in {activeList?.list_name || "My Providers"}.
          </p>
          <Link to="/my-providers" className="text-gold-400/60 hover:text-gold-400 text-xs mt-3 inline-block underline transition-colors">
            Manage My Providers →
          </Link>
        </div>
      )}

      <div className="space-y-3">
        {(isPro ? filteredResults : filteredResults.slice(0, 50)).map((physician) => (       
          <div
            key={physician.npi}
            className="card hover:border-gold-500/30 hover:bg-navy-700/50 transition-all group relative"
          >
            <Link
              to={`/physician/${physician.npi || "unknown"}?name=${encodeURIComponent(physician.firstName + " " + physician.lastName)}&state=${physician.state || ""}`}
              className="block"
            >              
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-white font-medium group-hover:text-gold-300 transition-colors">
                      {physician.firstName} {physician.lastName}
                    </h2>
                    {excludedNpis.get(physician.npi)?.oig && (
                      <span className="badge bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                        ⚠ OIG Excluded
                      </span>
                    )}
                    {excludedNpis.get(physician.npi)?.sam && (
                      <span className="badge bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs">
                        ⚠ SAM Excluded
                      </span>
                    )}
                  </div>                  
                  <p className="text-white/40 text-sm mt-0.5">
                    {physician.specialty} · {physician.city}{physician.state ? `, ${physician.state}` : ""}
                  </p>
                  {physician.npi && (
                    <p className="text-white/20 text-xs mt-0.5 font-mono">NPI: {physician.npi}</p>
                  )}
                </div>
                <span className="text-white/30 group-hover:text-white/60 ml-4 mt-1">›</span>
              </div>
              <div className="flex gap-3 mt-3 flex-wrap items-center">
                <span className="badge bg-gold-500/10 border border-gold-500/20 text-gold-300">
                  {formatCurrency(physician.totalPayments)}
                </span>
                <span className="badge bg-white/5 border border-white/10 text-white/50">
                  {physician.paymentCount} payment{physician.paymentCount !== 1 ? "s" : ""}
                </span>
                {physician.products.length > 0 && (
                  <span className="badge bg-white/5 border border-white/10 text-white/50">
                    {physician.products[0]}{physician.products.length > 1 ? ` +${physician.products.length - 1}` : ""}
                  </span>
                )}
              </div>
              </Link>
            <div className="mt-3 flex items-center justify-between gap-2">
              {/* ── Data flags ──────────────────────────────────────────── */}
              <div className="flex gap-2 flex-wrap">
                <span className="badge bg-gold-500/10 border border-gold-500/20 text-gold-400/80 text-xs">
                  💊 Payments
                </span>
                {ownershipNpis.has(physician.npi) && (
                  <span className="badge bg-amber-500/10 border border-amber-500/20 text-amber-400/80 text-xs">
                    🏦 Ownership
                  </span>
                )}
                {researchNpis.has(physician.npi) && (
                  <span className="badge bg-teal-500/10 border border-teal-500/20 text-teal-400/80 text-xs">
                    🔬 Research
                  </span>
                )}
                {(excludedNpis.get(physician.npi)?.oig || excludedNpis.get(physician.npi)?.sam) && (
                  <span className="badge bg-red-500/10 border border-red-500/20 text-red-400/80 text-xs">
                    ⚠ Excluded
                  </span>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
              <WatchProviderButton
                npi={physician.npi}
                providerName={physician.firstName + " " + physician.lastName}
                initialWatched={watchedNpis.get(physician.npi) || false}
                onWatchChange={(npi, watched) => {
                  setWatchedNpis((prev) => new Map(prev).set(npi, watched));
                  if (watched) {
                    setWatchedProviders((prev) => [...prev, { npi, provider_name: physician.firstName + " " + physician.lastName }]);
                  } else {
                    setWatchedProviders((prev) => prev.filter((p) => p.npi !== npi));
                  }
                }}
              />
              <CompareButton provider={physician} />
              </div>
            </div>
          </div>          
        ))}
      </div>
        {!isPro && results.length > 50 && (
          <div className="card border-gold-500/20 bg-gold-500/5 text-center py-6 mt-3">
            <p className="text-gold-400 font-medium text-sm mb-1">
              🔒 {results.length - 50} more providers hidden
            </p>
            <p className="text-white/40 text-xs mb-3">
              Upgrade to Pro to see up to 200 providers or download the full list as a CSV
            </p>
            <button
              onClick={() => {
                setUpgradeMessage("You're seeing the top 50 providers. Upgrade to Pro to see up to 200 providers, or download the full list as a CSV.");
                setShowUpgradeModal(true);
              }}
              className="btn-primary text-xs px-4 py-2"
            >
              Upgrade to Pro
            </button>            
          </div>
        )}
      {!loading && results.length > 0 && (
        <>
          <p className="text-white/20 text-xs text-center mt-8">
            {isPro
              ? `Showing top 200 of ${summary?.totalPhysicians?.toLocaleString()} providers`
              : `Showing top 50 of ${summary?.totalPhysicians?.toLocaleString()} providers — upgrade to Pro for top 200 or a full list`
            } · Source: CMS Open Payments
          </p>
          {isPro && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <button
                onClick={() => exportProvidersCsv(results, displayName)}
                className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors"
              >
                ⬇ Export visible
              </button>
              <button
                onClick={() => handleFullExport(companyQuery, year)}
                className="text-gold-400/80 hover:text-gold-400 text-xs transition-colors font-medium"
              >
                ⬇ Export all providers
              </button>
            </div>
          )}
        </>
      )}

{/* Back to top */}
      {results.length > 0 && (
        <div className="flex justify-center mt-6">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="text-white/30 hover:text-white/60 text-xs transition-colors"
          >
            ↑ Back to top
          </button>
        </div>
      )}

{/* Watching List */}
      {isSignedIn && isPro && results.length > 0 && (        
        <div id="watching-list" className="card mt-6">          
          <button
            onClick={() => setWatchListOpen(!watchListOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-white/60 text-sm">🔔 Provider Watch List</span>
              {watchedProviders.length > 0 && (
                <span className="bg-gold-500/20 border border-gold-500/30 text-gold-400 text-xs px-2 py-0.5 rounded-full">
                  {watchedProviders.length}
                </span>
              )}
            </div>
            <span className="text-white/30 text-xs">{watchListOpen ? "▲" : "▼"}</span>
          </button>
          {watchListOpen && (
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto pr-1">
              {watchedProviders.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-4">
                  You aren't watching any providers yet. Click "🔕 Watch" on any provider to add them.
                </p>
              ) : (
                watchedProviders.map((p) => (
                  <div key={p.npi} className="flex items-center justify-between bg-navy-700/50 border border-white/5 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="min-w-0">
                        <Link
                          to={`/physician/${p.npi}?name=${encodeURIComponent(p.provider_name)}`}
                          className="text-white/80 text-sm hover:text-gold-300 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {p.provider_name}
                        </Link>
                        <p className="text-white/30 text-xs font-mono mt-0.5">NPI: {p.npi}</p>
                      </div>
                      <CompareButton provider={{
                        npi: p.npi,
                        firstName: (p.provider_name || "").split(" ")[0] || "",
                        lastName: (p.provider_name || "").split(" ").slice(1).join(" ") || "",
                        state: p.state || "",
                        totalPayments: p.total_payments || 0,
                      }} />
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      <div className="flex gap-1">
                        {p.alert_payments && (
                          <span className="text-white/30 text-xs">💊 Payments</span>
                        )}
                        {p.alert_exclusions && (
                          <span className="text-white/30 text-xs ml-2">⚠ Exclusions</span>
                        )}
                      </div>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          await unwatchProvider(user.id, p.npi);
                          setWatchedProviders((prev) => prev.filter((w) => w.npi !== p.npi));
                          setWatchedNpis((prev) => new Map(prev).set(p.npi, false));
                        }}
                        className="text-white/20 hover:text-red-400 text-xs transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

</main>

    </>
  );
}

function exportProvidersCsv(providers, companyName) {
  const rows = [["First Name", "Last Name", "Specialty", "City", "State", "Total Payments", "Payment Count"]];
  for (const p of providers) {
    rows.push([
      p.firstName || "",
      p.lastName || "",
      p.specialty || "",
      p.city || "",
      p.state || "",
      p.totalPayments ? p.totalPayments.toFixed(2) : "0.00",
      p.paymentCount || 0,
    ]);
  }
  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${companyName.replace(/\s+/g, "_")}_providers.csv`;
  a.click();
}